import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { YStack } from "tamagui";
import { z } from "zod";

import { WorkflowType } from "@/api/generated/models";
import { useSendNoriMessage } from "@/api/mutations/conversation";
import {
  Button,
  Footer,
  Heading,
  Paragraph,
  Text,
  TextArea,
} from "@/components";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";
import { FLOW_NAME } from "./useAIDoctorProgression";

const rfeSchema = z.object({
  reason: z.string().trim().min(1, "Please describe your symptoms or concerns"),
});

type RFEFormData = z.infer<typeof rfeSchema>;

export const RFEScreen = ({ onContinue }: { onContinue: () => void }) => {
  const ctx = useUserProgression();
  const workflowType =
    ctx?.progression.flow_name === FLOW_NAME[WorkflowType.diagnosis]
      ? WorkflowType.diagnosis
      : WorkflowType.rx_renewal;

  const { sendNoriMessage, isPending } = useSendNoriMessage();

  const { control, handleSubmit } = useForm<RFEFormData>({
    resolver: zodResolver(rfeSchema),
    defaultValues: { reason: "" },
  });

  const handleContinue = handleSubmit(({ reason }) => {
    sendNoriMessage(reason, {
      // TODO: update this with other agents for labs, etc.
      agent:
        workflowType === WorkflowType.diagnosis
          ? "dx_intake_agent"
          : "generalist",
      workflowType,
      onConversationCreation: onContinue,
    });
  });

  return (
    <YStack gap="$200" flex={1} paddingHorizontal="$200" paddingBottom="$200">
      <YStack gap="$100">
        <Heading size="$xl">What brings you in today?</Heading>
        <Paragraph color="$textSecondary" size="$s">
          Please describe your symptoms or concerns.{" "}
          <Text color="$textSecondary" size="$s" bold>
            If this is a medical emergency, please call 911 or go to the nearest
            emergency room.
          </Text>
        </Paragraph>
      </YStack>

      <YStack gap="$200" flex={1}>
        <Controller
          control={control}
          name="reason"
          render={({ field, fieldState }) => (
            <TextArea.Controlled
              placeholder="Describe your symptoms or concerns..."
              value={field.value}
              onChangeText={field.onChange}
              onBlur={field.onBlur}
              errorText={fieldState.error?.message}
              frameProps={{ minHeight: 120 }}
            />
          )}
        />
      </YStack>

      <Footer mode="dialog">
        <Button
          mode="primary"
          constrained
          onPress={handleContinue}
          loading={isPending}
          dataTrackingId="rfe-continue-button"
        >
          Continue
        </Button>
      </Footer>
    </YStack>
  );
};
